import type { PlayerState } from '@crowdset/domain';
import type { PlayerAdapter, PlayerEvents } from './types';

/**
 * DEMO MEDIA ADAPTER — used only when the server runs with MEDIA_MODE=fake (local demo / CI).
 * It simulates a player clock so sync logic can be exercised deterministically. It never
 * pretends to be YouTube; the view renders a visible "Demo media" label.
 */
export class FakeAdapter implements PlayerAdapter {
  readonly kind = 'fake' as const;
  private events: PlayerEvents | null = null;
  private st: PlayerState = 'unstarted';
  private baseMs = 0;
  private startedAt = 0;
  private durationMs = 0;
  videoId: string | null = null;
  volume = 70;
  muted = false;
  private listeners = new Set<() => void>();
  private endTimer: ReturnType<typeof setTimeout> | null = null;

  async mount(_el: HTMLElement, events: PlayerEvents): Promise<void> {
    this.events = events;
    queueMicrotask(() => events.onReady());
  }

  subscribe(fn: () => void) {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  }
  private emit() {
    for (const l of this.listeners) l();
  }
  private setState(s: PlayerState) {
    this.st = s;
    this.events?.onState(s);
    this.emit();
  }
  private scheduleEnd() {
    if (this.endTimer) clearTimeout(this.endTimer);
    if (this.st !== 'playing') return;
    const remaining = this.durationMs - this.currentTimeMs();
    this.endTimer = setTimeout(
      () => {
        this.baseMs = this.durationMs;
        this.setState('ended');
      },
      Math.max(0, remaining),
    );
  }

  load(videoId: string, startMs: number, autoplay: boolean, durationMs: number): void {
    // A newly loaded video must never "end" because of the previous video's timer (a real
    // embed doesn't emit ended for a video it has already replaced).
    if (this.endTimer) clearTimeout(this.endTimer);
    this.videoId = videoId;
    this.durationMs = durationMs;
    this.baseMs = Math.min(Math.max(0, startMs), durationMs);
    if (autoplay && isAutoplayBlockedForTests() && !hasUserActivation()) {
      this.setState('cued');
      queueMicrotask(() => this.events?.onAutoplayBlocked());
      return;
    }
    if (autoplay) {
      this.setState('buffering');
      setTimeout(() => {
        if (this.videoId !== videoId) return;
        this.startedAt = performance.now();
        this.setState('playing');
        this.scheduleEnd();
      }, 120);
    } else {
      this.setState('cued');
    }
  }
  play() {
    if (!this.videoId || this.st === 'playing') return;
    if (isAutoplayBlockedForTests() && !hasUserActivation()) {
      queueMicrotask(() => this.events?.onAutoplayBlocked());
      return;
    }
    this.startedAt = performance.now();
    this.setState('playing');
    this.scheduleEnd();
  }
  pause() {
    if (this.st !== 'playing') return;
    this.baseMs = this.currentTimeMs();
    this.setState('paused');
    if (this.endTimer) clearTimeout(this.endTimer);
  }
  stop() {
    this.videoId = null;
    this.baseMs = 0;
    if (this.endTimer) clearTimeout(this.endTimer);
    this.setState('unstarted');
  }
  seek(ms: number) {
    this.baseMs = Math.min(Math.max(0, ms), this.durationMs);
    this.startedAt = performance.now();
    if (this.st === 'playing') {
      // Mirror the embed: a seek while playing briefly buffers, then reports playing again.
      this.setState('buffering');
      this.baseMs = Math.min(Math.max(0, ms), this.durationMs);
      setTimeout(() => {
        if (this.st !== 'buffering') return;
        this.startedAt = performance.now();
        this.setState('playing');
        this.scheduleEnd();
      }, 60);
      return;
    }
    this.emit();
    this.scheduleEnd();
  }
  currentTimeMs(): number {
    if (this.st === 'playing')
      return Math.min(this.durationMs, this.baseMs + (performance.now() - this.startedAt));
    return this.baseMs;
  }
  get duration() {
    return this.durationMs;
  }
  state(): PlayerState {
    return this.st;
  }
  setVolume(v: number) {
    this.volume = v;
    this.emit();
  }
  setMuted(m: boolean) {
    this.muted = m;
    this.emit();
  }
  destroy() {
    if (this.endTimer) clearTimeout(this.endTimer);
    this.listeners.clear();
    this.events = null;
  }
}

function isAutoplayBlockedForTests(): boolean {
  try {
    return localStorage.getItem('crowdset.fakeAutoplayBlocked') === '1';
  } catch {
    return false;
  }
}

/** Mirrors browser autoplay policy: playback started from a recent user gesture is allowed. */
function hasUserActivation(): boolean {
  try {
    return navigator.userActivation?.isActive ?? false;
  } catch {
    return false;
  }
}
